import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Grid,
  useTheme
} from '@mui/material';
import {
  Add,
  Close,
  Delete,
  CloudUpload,
  PictureAsPdf,
  Print,
  ArrowBackIos,
  ArrowForwardIos,
  ZoomIn,
  ZoomOut,
  RotateRight,
  Download
} from '@mui/icons-material';
import api from '../../services/api';

const FileUploadPopup = ({
  open,
  onClose,
  onFilesSelected,
  title = "Upload Files",
  maxFiles = 6,
  accept = "image/*",
  multiple = true,
  existingFiles = []
}) => {
  const theme = useTheme();
  const [files, setFiles] = useState(existingFiles);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fileInputRef = useRef(null);

  // Update files when existingFiles prop changes
  useEffect(() => {
    setFiles(existingFiles);
    
    // Fetch images for uploaded files that have paths
    const fetchImages = async () => {
      const newBlobUrls = {};
      for (const file of existingFiles) {
        if (file.path && file.type && file.type.startsWith('image/')) {
          const blobUrl = await fetchImageAsBlob(file.path);
          if (blobUrl) {
            newBlobUrls[file.path] = blobUrl;
          }
        }
      }
      setImageBlobUrls(newBlobUrls);
    };
    
    fetchImages();
  }, [existingFiles]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    if (files.length + selectedFiles.length > maxFiles) {
      alert(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    const newFiles = selectedFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setFiles(prev => [...prev, ...newFiles]);
    event.target.value = '';
  };

  const handleRemoveFile = async (fileId) => {
    const fileToRemove = files.find(f => f.id === fileId);
    
    if (fileToRemove && fileToRemove.isExisting && fileToRemove.id !== 'photograph' && fileToRemove.id !== 'signature') {
      try {
        const deletePayload = {
          data: JSON.stringify({
            id: fileToRemove.id,
            active: false
          }),
          target: "society.models.master.member.MemberDocument",
          compressed: false
        };

        const response = await api.post(`${process.env.REACT_APP_API_URL}/file/delete`, deletePayload, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': sessionStorage.getItem('token') || '',
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error('❌ DELETE: API call failed:', error);
      }
    }

    setFiles(prev => {
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleRemoveFileByIndex = (index) => {
    setFiles(prev => {
      const fileToRemove = prev[index];
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = () => {
    onFilesSelected(files);
    onClose();
  };

  const handleClose = () => {
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    
    Object.values(imageBlobUrls).forEach(blobUrl => {
      URL.revokeObjectURL(blobUrl);
    });
    
    setFiles(existingFiles);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePrint = (file) => {
    const fileUrl = file.preview || (file.path ? imageBlobUrls[file.path] || file.path : null);

    if (!fileUrl) {
      console.error("No URL found for file:", file);
      return;
    }

    if (file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) {
      window.open(fileUrl, '_blank');
    } else {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print Image</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; }
                img { max-width: 100%; max-height: 100%; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${fileUrl}" onload="window.print(); setTimeout(window.close, 500);" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
  };
  
  const handleDownload = (file) => {
    const fileUrl = file.preview || (file.path ? imageBlobUrls[file.path] || file.path : null);
    if (!fileUrl) return;
    
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.name || (file.file && file.file.name) || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchImageAsBlob = async (imageUrl) => {
    try {
      const response = await api.get(imageUrl, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Failed to fetch image:', error);
      return null;
    }
  };

  const [imageBlobUrls, setImageBlobUrls] = useState({});

  // Preview Navigation Handlers
  const handleNext = (e) => {
    if (e) e.stopPropagation();
    setPreviewIndex((prev) => (prev + 1) % files.length);
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  const handlePrev = (e) => {
    if (e) e.stopPropagation();
    setPreviewIndex((prev) => (prev - 1 + files.length) % files.length);
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };
  
  const handleZoomIn = (e) => {
     if (e) e.stopPropagation();
     setZoom(prev => Math.min(prev + 0.5, 5));
  };
  
  const handleZoomOut = (e) => {
     if (e) e.stopPropagation();
     setZoom(prev => Math.max(prev - 0.5, 0.5));
  };
  
  const handleRotate = (e) => {
     if (e) e.stopPropagation();
     setRotation(prev => prev + 90);
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      e.preventDefault();
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleKeyDown = (e) => {
    if (previewIndex === null) return;
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'Escape') setPreviewIndex(null);
  };

  useEffect(() => {
    if (previewIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewIndex, files.length]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '400px',
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.85)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(12px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0px 10px 30px rgba(0, 0, 0, 0.5)'
            : '0px 10px 30px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        px: 3,
        pt: 3,
        backgroundColor: (theme) => theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.85)'
          : 'rgba(255, 255, 255, 0.85)',
        color: 'primary.main',
        borderRadius: '16px 16px 0 0',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
          {title}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.08)'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload up to {maxFiles} files. {files.length}/{maxFiles} files selected.
          </Typography>
          
          <Grid container spacing={2}>
            {files.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={file.id || `file-${index}`}>
                <Card
                  onClick={() => {
                        if (file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) {
                            handlePrint(file);
                        } else {
                            setPreviewIndex(index);
                             setPan({ x: 0, y: 0 }); // Reset pan on open
                        }
                  }}
                  sx={{
                    height: '200px',
                    width: '200px',
                    position: 'relative',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'row',
                    cursor: 'pointer',
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.04)'
                      : 'rgba(0, 0, 0, 0.02)',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
                      : '0px 2px 8px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0px 6px 20px rgba(0, 0, 0, 0.4)'
                        : '0px 4px 16px rgba(0, 0, 0, 0.12)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease-in-out'
                    }
                  }}
                >
                  {(file.preview || file.path || file.file) ? (
                    <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                      {(file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) ? (
                        <Box sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.04)'
                            : 'rgba(0, 0, 0, 0.02)',
                        }}>
                          <PictureAsPdf sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center', color: 'text.primary' }}>
                            PDF Document
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{
                          flex: 1,
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.04)'
                            : 'rgba(0, 0, 0, 0.02)',
                        }}>
                          <img
                          src={imageBlobUrls[file.path] || file.preview}
                          alt={file.name || (file.file && file.file.name)}
                          style={{
                            maxWidth: '80%',
                            maxHeight: '80%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', file.path || file.preview);
                            e.target.style.display = 'none';
                          }}
                        />
                        </Box>
                      )}
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: (theme) => theme.palette.mode === 'dark'
                          ? 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                          : 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        padding: '8px',
                        color: 'white',
                        borderRadius: '0 0 12px 12px',
                      }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 500
                          }}
                        >
                          {file.name || (file.file && file.file.name)}
                        </Typography>
                      </Box>
                        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(file);
                            }}
                            sx={{
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.8)',
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(255, 255, 255, 1)',
                              },
                              width: '28px',
                              height: '28px',
                            }}
                          >
                             {(file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) ? <Download sx={{ fontSize: '16px' }} /> : <Print sx={{ fontSize: '16px' }} />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              file.id ? handleRemoveFile(file.id) : handleRemoveFileByIndex(index);
                            }}
                            sx={{
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.8)',
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(255, 255, 255, 1)',
                              },
                              width: '28px',
                              height: '28px',
                            }}
                          >
                            <Delete sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Box>
                    </Box>
                  ) : (
                    <CardContent sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative'
                    }}>
                      {(file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) ? (
                        <PictureAsPdf sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                      ) : (
                        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          color: 'text.primary'
                        }}
                      >
                        {file.name || (file.file && file.file.name)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {file.size || formatFileSize(file.size)}
                      </Typography>

                        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(file);
                            }}
                            sx={{
                              color: 'primary.main',
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(255, 255, 255, 1)',
                              },
                              width: '28px',
                              height: '28px',
                            }}
                          >
                            {(file.type === 'application/pdf' || (file.file && file.file.type === 'application/pdf')) ? <Download sx={{ fontSize: '16px' }} /> : <Print sx={{ fontSize: '16px' }} />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              file.id ? handleRemoveFile(file.id) : handleRemoveFileByIndex(index);
                            }}
                            sx={{
                              color: 'error.main',
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(255, 255, 255, 1)',
                              },
                              width: '28px',
                              height: '28px',
                            }}
                          >
                            <Delete sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Box>
                    </CardContent>
                  )}
                </Card>
              </Grid>
            ))}

            {files.length < maxFiles && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  component="label"
                  sx={{
                    height: '200px',
                    width: '200px',
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.04)'
                      : 'rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
                      : '0px 2px 8px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0px 6px 20px rgba(0, 0, 0, 0.4)'
                        : '0px 4px 16px rgba(0, 0, 0, 0.12)',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 2
                  }}>
                    <Add sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                      Add File
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click to upload
                    </Typography>
                  </CardContent>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple={multiple}
                    accept={accept}
                    onChange={handleFileSelect}
                  />
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        py: 2,
        gap: 1,
        backgroundColor: (theme) => theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.85)'
          : 'rgba(255, 255, 255, 0.85)',
        borderRadius: '0 0 16px 16px',
        justifyContent: 'space-between',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              borderColor: 'error.dark',
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(244, 67, 54, 0.08)'
                : 'rgba(244, 67, 54, 0.04)',
              color: 'error.dark'
            }
          }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: 'primary.main',
            "&:hover": {
              backgroundColor: 'primary.dark',
            },
            color: "#fff",
            fontWeight: 600,
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0px 4px 12px rgba(33, 150, 243, 0.3)'
              : '0px 4px 12px rgba(33, 150, 243, 0.2)',
          }}
        >
          Save Files ({files.length})
        </Button>
      </DialogActions>

      {/* Preview Dialog */}
      <Dialog
        open={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'common.black',
            boxShadow: 'none',
            backgroundImage: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          height: '80vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 2
        }}>
           {/* Close Button */}
           <IconButton
            onClick={() => setPreviewIndex(null)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <Close />
          </IconButton>

          {/* Navigation Buttons */}
          {files.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <ArrowBackIos />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            </>
          )}
          
          {/* Controls */}
          {previewIndex !== null && files[previewIndex] && (
            <Box sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 2,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '24px',
                padding: '8px 16px',
            }}>
                 <IconButton onClick={handleZoomIn} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Zoom In">
                     <ZoomIn />
                 </IconButton>
                 <IconButton onClick={handleZoomOut} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Zoom Out">
                     <ZoomOut />
                 </IconButton>
                 <IconButton onClick={handleRotate} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Rotate">
                     <RotateRight />
                 </IconButton>
                 <IconButton onClick={() => handleDownload(files[previewIndex])} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Download">
                     <Download />
                 </IconButton>
            </Box>
          )}

          {/* Image/Content */}
          {previewIndex !== null && files[previewIndex] && (
            <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                <img
                  src={imageBlobUrls[files[previewIndex].path] || files[previewIndex].preview}
                  alt={files[previewIndex].name || (files[previewIndex].file && files[previewIndex].file.name)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                />
            </Box>
          )}

          {/* Footer/Caption */}
          <Box sx={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            p: 2, 
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
            color: 'white',
            textAlign: 'center'
          }}>
             <Typography variant="body1">
               {previewIndex !== null && files[previewIndex] && (files[previewIndex].name || (files[previewIndex].file && files[previewIndex].file.name))}
             </Typography>
             <Typography variant="caption" sx={{ opacity: 0.7 }}>
               {previewIndex + 1} / {files.length}
             </Typography>
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default FileUploadPopup;
